import type { components } from '@joyful/openapi/types.gen'
import type { EnumValueMeta } from '@joyful/utils'
import { createEnumSchema } from '@joyful/utils'
import type { LucideIcon } from 'lucide-react'
import {
  BotIcon,
  CheckIcon,
  CircleIcon,
  ContrastIcon,
  DollarSignIcon,
  ReceiptIcon,
  ShieldAlertIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react'
import type { ComponentType, SVGProps } from 'react'

import type { BadgeProps } from './Badge'
import type { TagDotProps } from './Tag'

interface EncounterEnrichmentStatusMetadata extends EnumValueMeta {
  badgeVariant: BadgeProps['variant']
  description: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
}

export const EncounterEnrichmentStatusSchema = createEnumSchema<
  components['schemas']['EncounterEnrichmentStatus'],
  EncounterEnrichmentStatusMetadata
>([
  {
    badgeVariant: 'warning',
    description:
      'Claims that the payer has not responded to yet. Follow-up recommended.',
    icon: DollarSignIcon,
    title: 'Awaiting Payer Response',
    value: 'AWAITING_PAYER_RESPONSE',
  },
  {
    badgeVariant: 'warning',
    description:
      'Claims that the payer has not responded to yet. Follow-up recommended.',
    icon: DollarSignIcon,
    title: 'Awaiting Primary Payer Response',
    value: 'AWAITING_PRIMARY_PAYER_RESPONSE',
  },
  {
    badgeVariant: 'warning',
    description:
      'Claims that the payer has not responded to yet. Follow-up recommended.',
    icon: DollarSignIcon,
    title: 'Awaiting Secondary Payer Response',
    value: 'AWAITING_SECONDARY_PAYER_RESPONSE',
  },
  {
    badgeVariant: 'warning',
    description:
      'Claims that the payer has not responded to yet. Follow-up recommended.',
    icon: DollarSignIcon,
    title: 'Awaiting Tertiary Payer Response',
    value: 'AWAITING_TERTIARY_PAYER_RESPONSE',
  },
  {
    badgeVariant: 'error',
    description:
      'Claims that were rejected by the payer or Clearinghouse. These claims should be corrected and resubmitted ASAP.',
    icon: ShieldAlertIcon,
    title: 'Rejected',
    value: 'REJECTED',
  },
  {
    badgeVariant: 'error',
    description:
      'Encounters that have a denial that have not been worked by the practice yet. These claims should either be appealed or re-submitted.',
    icon: ShieldAlertIcon,
    title: 'Denied',
    value: 'DENIED',
  },
  {
    badgeVariant: 'info',
    description:
      "Encounters that don't yet have a claim associated. These claims should either be billed to the patient or payer ASAP.",
    icon: DollarSignIcon,
    title: 'Not Billed Yet',
    value: 'NOT_BILLED_YET',
  },
  {
    badgeVariant: 'info',
    description:
      'Encounters that have an outstanding responsibility that needs to be collected.',
    icon: DollarSignIcon,
    title: 'Outstanding',
    value: 'OUTSTANDING',
  },
  {
    badgeVariant: 'success',
    description: 'Encounters that have been paid.',
    icon: DollarSignIcon,
    title: 'Paid',
    value: 'PAID',
  },
  {
    badgeVariant: 'info',
    description:
      'Encounters that have an outstanding patient responsibility (co-pay, deductible, co-insurance, etc.) that needs to be collected.',
    icon: UsersIcon,
    title: 'Patient Responsibility',
    value: 'PATIENT_RESPONSIBILITY',
  },
  {
    badgeVariant: 'neutral',
    description:
      'Encounters that have been written-off or voided. These may potentially be recoverable.',
    icon: ReceiptIcon,
    title: 'Write Off',
    value: 'WRITE_OFF',
  },
])

type EncounterLifecycleType = NonNullable<
  components['schemas']['EncounterAPIResponse']['lifecycle']
>['type']

interface EncounterLifecycleMetadata extends EnumValueMeta {
  icon: LucideIcon
}

export const EncounterLifecycleSchema = createEnumSchema<
  EncounterLifecycleType,
  EncounterLifecycleMetadata
>([
  {
    icon: CircleIcon,
    title: `Open`,
    value: 'OPEN',
  },
  {
    icon: ContrastIcon,
    title: `Resolution proposed`,
    value: 'RESOLUTION_PROPOSED',
  },
  {
    icon: CheckIcon,
    title: `Closed`,
    value: 'CLOSED',
  },
])

type EncounterBlockerType = NonNullable<
  components['schemas']['EncounterAPIResponse']['blocker']
>['type']

export const EncounterBlockerSchema = createEnumSchema<EncounterBlockerType>([
  { title: `None`, value: 'NONE' },
  { title: `Practice`, value: 'PRACTICE' },
  { title: `Payer`, value: 'PAYER' },
  { title: `Vendor`, value: 'VENDOR' },
])

interface EncounterOwnerMetadata extends EnumValueMeta {
  icon: LucideIcon
}

export const EncounterOwnerSchema = createEnumSchema<
  components['schemas']['EncounterOwner'],
  EncounterOwnerMetadata
>([
  { icon: BotIcon, title: `Agent`, value: 'AGENT' },
  { icon: UserIcon, title: `RCSM`, value: 'RCSM' },
  { icon: UserIcon, title: `RCM specialist`, value: 'RCM_SPECIALIST' },
  { icon: UserIcon, title: `BPO`, value: 'BPO' },
])

interface IngestionSourceTypeSchemaMetadata extends EnumValueMeta {
  info?: string
}

export const IngestionSourceTypeSchema = createEnumSchema<
  components['schemas']['IngestionSourceType'],
  IngestionSourceTypeSchemaMetadata
>([
  { title: 'AdvancedMD', value: 'ADVANCEDMD' },
  { title: 'Availity', value: 'AVAILITY' },
  { title: 'Candid', value: 'CANDID' },
  { title: 'Canvas', value: 'CANVAS' },
  { title: 'CentralReach', value: 'CENTRALREACH' },
  { title: 'CharmHealth', value: 'CHARMHEALTH' },
  { title: 'ClaimMD', value: 'CLAIMMD' },
  { title: 'CollaborateMD', value: 'COLLABORATEMD' },
  { title: 'EDI Insight', value: 'EDI_INSIGHT' },
  { title: 'ePACES', value: 'EPACES' },
  { title: 'Healthie', value: 'HEALTHIE' },
  { title: 'ICANotes', value: 'ICANOTES' },
  { title: 'Inovalon', value: 'INOVALON' },
  { title: 'InSync', value: 'INSYNC' },
  { title: 'MindWise', value: 'MINDWISE' },
  { title: 'Office Ally', value: 'OFFICE_ALLY' },
  { title: 'OptiMantra', value: 'OPTIMANTRA' },
  { title: 'PracticeQ', value: 'PRACTICEQ' },
  { title: 'SimplePractice', value: 'SIMPLE_PRACTICE' },
  { title: 'Stedi', value: 'STEDI' },
  { title: 'Tebra', value: 'TEBRA' },
  { title: 'TherapyNotes', value: 'THERAPY_NOTES' },
  { title: 'TriZetto', value: 'TRIZETTO' },
  { title: 'UHC TrackIt', value: 'UHC_TRACKIT' },
  { title: 'Valant', value: 'VALANT' },
  {
    info: `This payment was logged in Joyful Health through the **💰 Found payment** action.`,
    title: 'Joyful Health',
    value: 'JOYFUL_HEALTH',
  },
  { title: 'Waystar', value: 'WAYSTAR' },
])

export const PhoneCallDirectionSchema = createEnumSchema<
  components['schemas']['PhoneCallDirection']
>([
  { title: 'Inbound', value: 'inbound' },
  { title: 'Outbound', value: 'outbound' },
])

interface PhoneCallStatusMeta extends EnumValueMeta {
  variant: BadgeProps['variant']
}

export const PhoneCallStatusSchema = createEnumSchema<
  components['schemas']['PhoneCallStatus'],
  PhoneCallStatusMeta
>([
  { title: 'Busy', value: 'busy', variant: 'warning' },
  { title: 'Canceled', value: 'canceled', variant: 'neutral' },
  { title: 'Completed', value: 'completed', variant: 'success' },
  { title: 'Failed', value: 'failed', variant: 'error' },
  { title: 'In Progress', value: 'in_progress', variant: 'info' },
  { title: 'Initiated', value: 'initiated', variant: 'neutral' },
  { title: 'No Answer', value: 'no_answer', variant: 'warning' },
  { title: 'Ringing', value: 'ringing', variant: 'info' },
])

export type DropboxCategory = components['schemas']['DropboxCategory']

interface DropboxCategoryMetadata extends EnumValueMeta {
  dotVariant: NonNullable<TagDotProps['variant']>
  expandedLabel?: string
  label: string
}

export const DropboxCategorySchema = createEnumSchema<
  DropboxCategory,
  DropboxCategoryMetadata
>([
  {
    dotVariant: 'categoryBlue',
    expandedLabel: 'Explanation of benefits',
    label: 'EOB',
    title: 'EOB',
    value: 'EOB',
  },
  {
    dotVariant: 'categoryAmber',
    label: 'Reversal',
    title: 'Reversal',
    value: 'REVERSAL',
  },
  {
    dotVariant: 'categoryViolet',
    label: 'Appeal',
    title: 'Appeal decision',
    value: 'APPEAL_DECISION',
  },
  {
    dotVariant: 'categoryRed',
    expandedLabel: 'Provider out of network',
    label: 'OON',
    title: 'Provider out of network',
    value: 'PROVIDER_OUT_OF_NETWORK',
  },
  {
    dotVariant: 'categoryEmerald',
    label: 'Enrollment',
    title: 'In-network enrollment',
    value: 'IN_NETWORK_ENROLLMENT_CONFIRMATION',
  },
  {
    dotVariant: 'categorySky',
    expandedLabel: 'Payer request for information, records, or a form',
    label: 'Request',
    title: 'Payer request',
    value: 'PAYER_REQUEST',
  },
])

export const EncounterUpdateViolationCodeSchema = createEnumSchema<
  components['schemas']['EncounterUpdateViolationCode']
>([
  { title: 'Already snoozed', value: 'already_snoozed' },
  { title: 'Belongs to another account', value: 'belongs_to_account' },
  { title: 'Not found', value: 'not_found' },
  {
    title: 'Requires an assigned biller to you',
    value: 'requires_assigned_to_you_for_override',
  },
  {
    title: 'Requires an assignment for snooze',
    value: 'requires_assignment_for_snooze',
  },
  { title: 'Requires follow-up flag', value: 'requires_followup_flag' },
  {
    title: 'Requires a follow-up flag for override',
    value: 'requires_followup_flag_for_override',
  },
  {
    title: 'Requires an unassigned biller',
    value: 'requires_unassigned_biller',
  },
  {
    title: 'Requires a workflow status of new or none',
    value: 'requires_workflow_status_new_or_none',
  },
  { title: 'Can only be assigned to yourself', value: 'self_assign_only' },
  { title: 'Snooze date is in the past', value: 'snooze_date_in_past' },
  {
    title: 'Encounter is closed',
    value: 'snooze_encounter_closed',
  },
  {
    title: 'Encounter is resolved',
    value: 'snooze_encounter_resolved',
  },
  {
    title: 'Snooze date exceeds filing limit',
    value: 'snooze_exceeds_filing_limit',
  },
  {
    title: 'Must be flagged for follow-up to snooze',
    value: 'snooze_requires_followup_flag',
  },
])
